/**
 * AdVersionWidget — T9 «Наблюдение за регрессиями по версии клиента»
 * ========================================================================
 *
 * Помогает решить: нужен хотфикс/роллбэк конкретной версии клиента, если она
 * ломает доступность рекламы, досмотр или событие оплаты. Диапазон дат и
 * платформа — из общих фильтров дашборда, календарно (event_ts/event_date).
 *
 * Версии сворачиваются на сервере в топ-8 + Other (версии с <500
 * пользователей в день также уходят в Other на дневном графике) — здесь
 * только раскраска: новейшая версия акцентным цветом, старые — оттенками
 * серого, Other — пунктиром.
 *
 * Два блока:
 * 1) Small multiples по метрикам (engaged share, imps/user, completion rate,
 *    fail rate, paid/shown, eCPM), общая ось X — дата, линии — версии.
 * 2) Таблица версий за весь диапазон (агрегат), включая биллинговую выручку
 *    по версии УСТАНОВКИ (client_ver_i) отдельным блоком — см. комментарий в
 *    adVersionService.ts про несовпадение осей client_ver / client_ver_i.
 */

import React, { useMemo, useState } from 'react';
import { BaseWidget, useBaseWidget } from '../BaseWidget';
import { useAdsWidgetData } from '../../../hooks/useAdsGeo';
import { AdsService } from '../../../services/adsService';
import { ToggleGroup, ToggleGroupItem } from '../../ui/toggle-group';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell
} from '../../ui/table';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer } from 'recharts';
import { Tooltip } from '../../charts/OrderedTooltip';
import { ads as adsSpecs } from '@ggstat/chart-specs';

const versionSpecs = adsSpecs.versions;

const GRAY_SHADES = versionSpecs.grayShades;
const ACCENT_COLOR = versionSpecs.accentColor;
const OTHER_COLOR = versionSpecs.otherColor;
const OTHER_VERSION = versionSpecs.otherVersion;

const METRICS = versionSpecs.metrics;

const fmtInt = (v) => Number(v || 0).toLocaleString('ru-RU', { maximumFractionDigits: 0 });
const fmtPct = (v) => `${(Number(v || 0) * 100).toFixed(1)}%`;
const fmtPct2 = (v) => `${(Number(v || 0) * 100).toFixed(2)}%`;
const fmtNum1 = (v) => Number(v || 0).toLocaleString('ru-RU', { maximumFractionDigits: 1 });
const fmtMoney2 = (v) => `$${Number(v || 0).toFixed(2)}`;

const FORMATTERS = { pct: fmtPct, pct2: fmtPct2, num1: fmtNum1, money2: fmtMoney2 };

function versionColor(version, versionOrder, newestVersion) {
  if (version === OTHER_VERSION) return OTHER_COLOR;
  if (version === newestVersion) return ACCENT_COLOR;
  const idx = Math.max(0, versionOrder.indexOf(version));
  return GRAY_SHADES[idx % GRAY_SHADES.length];
}

/** Строки серии за платформу -> points[date] -> {version: metricValue}. */
function pivotSeries(series, platform, metricKey) {
  const byDate = new Map();
  for (const r of series) {
    if (r.platform !== platform) continue;
    if (!byDate.has(r.date)) byDate.set(r.date, { date: r.date });
    byDate.get(r.date)[r.version] = r[metricKey];
  }
  return Array.from(byDate.values()).sort((a, b) => a.date.localeCompare(b.date));
}

function VersionMetricChart({ metric, series, platform, versions, newestVersion }) {
  const points = useMemo(
    () => pivotSeries(series, platform, metric.key),
    [series, platform, metric.key]
  );
  const fmt = FORMATTERS[metric.fmt] || fmtInt;

  return (
    <div className="space-y-1">
      <p className="text-xs font-medium text-muted-foreground">{metric.label}</p>
      <ResponsiveContainer width="100%" height={180}>
        <LineChart data={points} margin={{ top: 4, right: 8, bottom: 4, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
          <XAxis dataKey="date" tick={{ fontSize: 10 }} minTickGap={20} />
          <YAxis tick={{ fontSize: 10 }} width={48} tickFormatter={fmt} />
          <Tooltip formatter={(v) => fmt(v)} />
          {versions.map((version) => (
            <Line
              key={version}
              type="monotone"
              dataKey={version}
              name={version}
              stroke={versionColor(version, versions, newestVersion)}
              strokeWidth={version === newestVersion ? 2.5 : 1.5}
              strokeDasharray={version === OTHER_VERSION ? '4 4' : undefined}
              dot={false}
              isAnimationActive={false}
              connectNulls={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

export function AdVersionWidget() {
  const versionsData = useAdsWidgetData(
    async (dashboardFilters, options) => AdsService.get('/versions', dashboardFilters, {}, options),
    []
  );

  const widgetProps = useBaseWidget(versionsData);
  const { data } = widgetProps;
  const series = useMemo(() => data?.series || [], [data]);
  const table = data?.table || [];
  const billedByInstall = data?.billedRevenueByInstallVersion || [];
  const meta = data?.meta;

  const platforms = useMemo(() => Array.from(new Set(series.map((r) => r.platform))).sort(), [series]);
  const [platformView, setPlatformView] = useState(null);
  const activePlatform = platformView && platforms.includes(platformView) ? platformView : platforms[0];

  const versions = useMemo(() => {
    const top = meta?.topVersions || [];
    const hasOther = series.some((r) => r.version === OTHER_VERSION);
    return hasOther ? [...top, OTHER_VERSION] : top;
  }, [meta, series]);

  const newestVersion = meta?.newestVersion || null;

  return (
    <BaseWidget
      {...widgetProps}
      title="Версии клиента: наблюдение за регрессиями"
      subtitle="Вовлечение, досмотр, фейлы и eCPM по версии клиента — новейшая версия акцентом"
      emptyMessage="Нет данных за выбранный диапазон"
      skeletonRows={8}
    >
      <div className="space-y-4">
        {platforms.length > 1 && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Платформа для графика</span>
            <ToggleGroup type="single" value={activePlatform} onValueChange={(v) => v && setPlatformView(v)}>
              {platforms.map((p) => (
                <ToggleGroupItem key={p} value={p} aria-label={p}>
                  {p}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
          </div>
        )}

        {series.length > 0 && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {METRICS.map((metric) => (
                <VersionMetricChart
                  key={metric.key}
                  metric={metric}
                  series={series}
                  platform={activePlatform}
                  versions={versions}
                  newestVersion={newestVersion}
                />
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-3 text-xs">
              {versions.map((v) => (
                <span key={v} className="flex items-center gap-1.5">
                  <span
                    className="inline-block w-3 h-0.5 rounded"
                    style={{
                      backgroundColor: versionColor(v, versions, newestVersion),
                      borderTop: v === OTHER_VERSION ? '1px dashed #64748b' : undefined
                    }}
                  />
                  {v} {v === newestVersion && '(новейшая)'}
                </span>
              ))}
            </div>
            {meta && (
              <p className="text-xs text-muted-foreground">
                Порог свёртки в Other: &lt; {fmtInt(meta.minUsersPerDay)} пользователей/день
              </p>
            )}
          </>
        )}

        {table.length > 0 && (
          <div className="pt-2 border-t space-y-2">
            <p className="text-sm font-medium">Таблица версий за диапазон</p>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Платформа</TableHead>
                    <TableHead>Версия</TableHead>
                    <TableHead className="text-right">Пользователи</TableHead>
                    <TableHead className="text-right">Engaged share</TableHead>
                    <TableHead className="text-right">Imps/user</TableHead>
                    <TableHead className="text-right">Completion</TableHead>
                    <TableHead className="text-right">Fail rate</TableHead>
                    <TableHead className="text-right">Paid/shown</TableHead>
                    <TableHead className="text-right">eCPM</TableHead>
                    <TableHead className="text-right">Выручка</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {table.map((row) => (
                    <TableRow key={`${row.platform}-${row.version}`}>
                      <TableCell>{row.platform}</TableCell>
                      <TableCell className={row.isNewest ? 'font-semibold' : undefined}>
                        {row.version} {row.isNewest && '★'}
                      </TableCell>
                      <TableCell className="text-right">{fmtInt(row.users)}</TableCell>
                      <TableCell className="text-right">{fmtPct(row.engagedShare)}</TableCell>
                      <TableCell className="text-right">{fmtNum1(row.impsPerUser)}</TableCell>
                      <TableCell className="text-right">{fmtPct(row.completionRate)}</TableCell>
                      <TableCell className="text-right">{fmtPct2(row.failRate)}</TableCell>
                      <TableCell className="text-right">{fmtPct(row.paidShownRate)}</TableCell>
                      <TableCell className="text-right">{fmtMoney2(row.ecpm)}</TableCell>
                      <TableCell className="text-right">${fmtInt(row.revenue)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        {billedByInstall.length > 0 && (
          <div className="pt-2 border-t space-y-2">
            <p className="text-sm font-medium">
              Биллинговая выручка по версии установки (client_ver_i)
            </p>
            <p className="text-xs text-muted-foreground">
              Отдельная ось от версии «на момент события» выше — версия установки не меняется у
              игрока задним числом, поэтому строки не сопоставляются 1:1 со строками таблицы.
            </p>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Версия установки</TableHead>
                    <TableHead className="text-right">Выручка</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {billedByInstall.slice(0, versionSpecs.billedTopN).map((row) => (
                    <TableRow key={row.clientVerI}>
                      <TableCell>{row.clientVerI || '—'}</TableCell>
                      <TableCell className="text-right">${fmtInt(row.revenue)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        {series.length === 0 && (
          <div className="text-center py-8 text-sm text-muted-foreground">
            Нет данных за выбранный диапазон
          </div>
        )}
      </div>
    </BaseWidget>
  );
}

export default AdVersionWidget;
